import {CleanroomSequenceOrderData} from '@/modules/cleanroom/hooks/use-cleanroom-sequence-batches'
import {ScannableItem} from '@/modules/cleanroom/state-machine'
import {formatTreatmentLabels} from '@/modules/patients-therapies/functions'
import {getPatientFullName} from '@/modules/patients/functions'
import {getVialTotalDrug} from '@/modules/products/trade-names/functions'
import {
  ABORT_CONSUMABLE_STATUSES,
  ABORT_DRUG_STATUSES,
  AbortMaterialKind,
  AbortMaterialStatus,
  AbortReason,
} from '@cypro/engine/mutators'
import {PatientTherapySequence} from '@cypro/engine/schema'
import {TFnType} from '@tolgee/react'

export interface AbortMaterialRow {
  /** Unique per physical unit; multiple units share the same itemId. */
  key: string
  itemId: string
  kind: AbortMaterialKind
  name: string
  isActiveSubstance: boolean
  /** Whether a remaining weight/volume input applies (e.g. not for whole-bag returns). */
  trackRemainingWeightVolume: boolean
  initialWeightVolume?: number
}

export function getAbortReasonOptions(t: TFnType) {
  return Object.values(AbortReason.enum).map(reason => ({label: t(`abort_reason.${reason}`), value: reason}))
}

export function getAbortStatusOptions(t: TFnType, isActiveSubstance: boolean, kind: AbortMaterialKind) {
  const statuses =
    kind === 'return'
      ? ABORT_DRUG_STATUSES.filter(s => s !== AbortMaterialStatus.enum.PARTIALLY_CONSUMED)
      : isActiveSubstance
        ? ABORT_DRUG_STATUSES
        : ABORT_CONSUMABLE_STATUSES
  return statuses.map(status => ({label: t(`abort_material_status.${status}`), value: status}))
}

export function defaultAbortStatus(isActiveSubstance: boolean): AbortMaterialStatus {
  return isActiveSubstance ? AbortMaterialStatus.enum.UNUSED : AbortMaterialStatus.enum.INTACT
}

export function buildAbortMaterialRows(
  scannableItems: ScannableItem[],
  order: CleanroomSequenceOrderData
): AbortMaterialRow[] {
  return scannableItems.flatMap(item => {
    // untracked solvent requirements have no batch to return/discard
    if (item.kind === 'solventRequirement') return []
    if (item.quantity <= 0) return []

    const unitCount = item.quantity
    const batch = order.flatBatches.find(b => b.id === (item.batchId ?? item.id))
    const restVial = order.restVialEntries.find(r => r.restVialId === item.id)
    // category batches carry a "#<category>" suffix in `id`; the mutator expects the raw entity id
    const itemId = item.batchId ?? item.id

    let unit: Omit<AbortMaterialRow, 'key'>

    if (item.kind === 'restVial') {
      unit = {
        itemId,
        kind: 'restVial',
        name: item.name,
        isActiveSubstance: true,
        trackRemainingWeightVolume: true,
        initialWeightVolume: restVial?.amount ?? undefined,
      }
    } else if (item.kind === 'return') {
      unit = {
        itemId,
        kind: 'return',
        name: item.name,
        isActiveSubstance: true,
        trackRemainingWeightVolume: false,
      }
    } else if (item.kind === 'consumable' || item.kind === 'carrier') {
      unit = {
        itemId,
        kind: 'batch',
        name: item.name,
        isActiveSubstance: false,
        trackRemainingWeightVolume: false,
      }
    } else {
      unit = {
        itemId,
        kind: 'batch',
        name: item.name,
        isActiveSubstance: true,
        trackRemainingWeightVolume: true,
        initialWeightVolume: getVialTotalDrug(batch?.tradeName) || undefined,
      }
    }

    return Array.from({length: unitCount}, (_, unitIndex) => ({
      ...unit,
      key: `${item.id}#${item.category ?? ''}#${unitIndex}`,
    }))
  })
}

export function buildAbortDescription(sequence: PatientTherapySequence, t: TFnType): string {
  const labels = formatTreatmentLabels(sequence, t)
  const patient = getPatientFullName(sequence.patient)

  const medicines = labels.medicines.map(m => `${m.name} ${m.dosage}`).join(', ')
  const carrier =
    sequence.volume && sequence.carrierSolution?.name
      ? ` ${sequence.volumeMode} ${sequence.volume} ml ${sequence.carrierSolution.name}`
      : ''
  const prescription = `${medicines}${carrier}`

  return t('abort_description', {patient, prescription})
}
